// The JSP on Firebase. Pure: the task, the asset and the QEW's answers in; the rail, the verdict and the record out.
// Design: MOCKUP_JSP_APPROVED.html rev 8 (approved 2026-10-08), JSP_ADJUSTMENTS.md items 1 to 32,
// RISK_ENGINE_DECISIONS.md 1 to 51 and RISK_ENGINE_DECISIONS_52_PLUS.md 52 to 61.
// Rev 9 (JSP_REV9_WORDS.md, approved 2026-10-09): Isolation Plan, Lock Out 1 to 6, task questions placed
// where they act, De-Energize This Job, switching at Need Power?, Tier 1 gloves, re-energize checks.
// Rev 10 (TASK_SIZING_WORDS.md Section 5, decisions 62 to 68, agreed 2026-10-09): the breaker reset door split,
// the RAB line, the first-operation question, the work-area question, the binding-handle line, the wire question,
// the condition lines, DRAFT lines hidden.
// The live JSP (esp-redirect node-jsp.js @ 9f451f0) is the baseline. Task words come from jsp-content-data.ts.
// Nothing here reads or writes the database. Shared by the JSP screens, the server and the unit tests.

import { GLOVE_DEFECTS, gloveCardOf, gloveFailProblem, usDate, type GloveView } from "./gloves";
import type { QewTrainingCard } from "./qew-training";
import type { AssetDoc } from "./guest-asset";
import { LIVE_TASK_CONTENT } from "./jsp-content-data";
import { ldlSequence, verdict, type FieldAssessment, type RaiseLevel, type Verdict, type VerdictInput, type VerdictTaskType } from "./risk-verdict";

// ---- Task content ----

/** A task question's answer. Don't Know is offered only where a question lists it (decision 64). */
export type PromptAnswer = "yes" | "no" | "dontKnow";
export const PROMPT_ANSWER_LABEL: Record<PromptAnswer, string> = { yes: "Yes", no: "No", dontKnow: "Don’t Know" };

export interface JspPrompt {
  k: string;
  q: string;
  /** The answers offered, in order. Yes and No unless the question adds Don't Know. */
  opts: PromptAnswer[];
  /** The answers that show the why line. */
  bad: PromptAnswer[];
  why: string;
  /** True when a bad answer needs Acknowledge (or De-Energize This Job) before he can move on. */
  escal: boolean;
  /** Where it shows. start: Job, Before You Start. need: Need Power?. place: PPE (Tier 1), Do The Work (Tier 2), Isolation Plan (Tier 3). */
  at: "start" | "need" | "place";
  /** A bad answer offers De-Energize This Job in place of Acknowledge. */
  deen: boolean;
}

/**
 * Rev 9 placement of the live task questions (JSP_FLOW_REVIEW findings 1 to 3).
 * Key: task name, then question key. Anything not listed shows at its place.
 * A why line is replaced only where the live words said "this becomes Tier 2".
 */
const PROMPT_PLACE: Record<string, Record<string, { at?: JspPrompt["at"]; deen?: boolean; escal?: boolean; q?: string; why?: string; drop?: boolean }>> = {
  "Fuse Replacement": { cause: { at: "start" } },
  "Circuit Breaker Reset": {
    causeKnown: { at: "start" },
    // Rev 10 item 1 (decision 62, R1): the panelboard door to a dead-front breaker is normal (AF-C4).
    doorRequired: { at: "start", escal: false, q: "Do you need to open the panelboard door to reach the handle?", why: "Normal. The dead-front stays on. Your hand goes on the handle only." },
  },
  "Overload Reset": {
    causeKnown: { at: "start" },
    repeatedTrips: { at: "start" },
    cooldown: { at: "start" },
    doorRequired: { at: "start", deen: true, why: "The only safe way is de-energized: open the disconnect, verify dead, reset with gloves and an insulated tool." },
  },
  "Energized Inspection — Doors Open": { justified: { at: "start", deen: true } },
  "Voltage Testing": { live: { at: "need", deen: true } },
  "Current Measurements": { single: { deen: true } },
  "IR Thermography Survey": { load: { drop: true } },
  "Starter Replacement": { control: { q: "Is control power identified as its own source?" } },
};

/** Tier 1 tasks where a hand goes on the equipment. Glove inspection always shows (rev 9 item 5). */
const HAND_ON = new Set(["Circuit Breaker Reset", "Overload Reset"]);

/** Decision 64: the first-operation question. Yes or Don't Know raises the arc row. */
export const FIRST_OPERATION_KEY = "firstOperation";
const FIRST_OPERATION: JspPrompt = {
  k: FIRST_OPERATION_KEY,
  q: "Has anyone worked on this equipment since it last ran?",
  opts: ["yes", "no", "dontKnow"],
  bad: ["yes", "dontKnow"],
  why: "First operation after work. Arc-rated PPE. Stand to the side. Turn your face away.",
  escal: false,
  at: "start",
  deen: false,
};

/** Decision 67: the wire you can't trace. Shows in the Isolation Plan. No raise. */
const UNTRACED_WIRE: JspPrompt = {
  k: "untracedWire",
  q: "Will you cut or move a wire you can’t trace end to end?",
  opts: ["yes", "no"],
  bad: ["yes"],
  why: "Test it dead where you cut. Then cut.",
  escal: false,
  at: "place",
  deen: false,
};

/** Rev 10 questions added to the live task content, in the order they show. */
const PROMPT_ADD: Record<string, JspPrompt[]> = {
  "Circuit Breaker Reset": [
    {
      k: "deadfrontOff",
      q: "Will you remove the dead-front to reach the handle?",
      opts: ["yes", "no"],
      bad: ["yes"],
      why: "Removing the dead-front exposes energized bus. Shock boundaries active. Reset it de-energized.",
      escal: true,
      at: "start",
      deen: true,
    },
    FIRST_OPERATION,
  ],
  "Overload Reset": [FIRST_OPERATION],
  "Control Wiring Modification": [UNTRACED_WIRE],
  "Conductor Termination": [UNTRACED_WIRE],
};

/** Rev 10 item 1: Circuit Breaker Reset work-practice lines that said the panel stays closed. */
const CONTROL_FIX: Record<string, Record<string, string>> = {
  "Circuit Breaker Reset": { "Handle only — panel stays closed.": "Handle only. Dead-front stays on." },
};
const DOOR_FIX: Record<string, string> = {
  "Circuit Breaker Reset": "If the dead-front must come off to reach the handle, stop. De-energize this job.",
};

/** Decision 63: tasks that lean on decision 62. One line at PPE And Boundary. */
const RAB_TASKS = new Set(["VFD Parameters", "PLC Interface Monitoring", "IR Thermography Survey", "Energized Inspection — Doors Open"]);
export const RAB_LINE = "Stay outside the RAB. Crossing it needs an EEWP.";

/** Decision 68 condition lines. */
export const ESCORT_LINE = "Escort only. Never inside the RAB.";
export const EXPOSE_LINES = ["Can’t see it? Don’t reach for it.", "Never defeat an interlock."] as const;
/** Decision 66: Lock Out step 2 on every lockout, any tier (Robert 2026-10-09: any binding handle is a concern). */
export const BINDING_HANDLE_LINE = "Handle binds? Don’t force it. Isolate upstream.";
/** Decision 65: the work-area question on every Tier 3 job. */
export const WORK_AREA_QUESTION = "Will anything in your work area stay energized? (bus, line side, adjacent breakers)";
export const LIVE_PARTS_LINE = "Live parts remain. PPE stays on until the work is done.";

/** A DRAFT placeholder from the live table is never shown (rev 10 item 9). */
const isDraft = (x: string) => x.includes("DRAFT");

/** Lock Out checklist, rev 9. Readable keys go on the record. */
export const LOCK_STEPS = ["notify", "interruptLoad", "bladesVerified", "storedReleased", "lotoApplied", "tryStart"] as const;
/** Close pill, Returned To Service (rev 9 item 6). */
export const REENERGIZE_CHECKS = [
  ["toolsAndGrounds", "Tools and grounds removed"],
  ["personnelClear", "Everyone clear of the boundary"],
  ["ppeOn", "PPE on to re-energize"],
  ["ownLocksOff", "Each lock removed by the person who placed it"],
] as const;

export interface JspTask {
  taskId: string;
  name: string;
  tier: 1 | 2 | 3;
  taskType: VerdictTaskType;
  /** From the task record (Firebase), never the live table. */
  sopNumber: string | null;
  hasContent: boolean;
  prompts: JspPrompt[];
  /** Tier 1: a hand goes on the equipment (breaker handle, overload reset). */
  handOn: boolean;
  /** Decision 63: "Stay outside the RAB" shows at PPE And Boundary. */
  rabLine: boolean;
  // Tier 1
  controls: string[];
  door: string;
  doorBranch: { q: string; reasons: { v: string; label: string }[]; affirm: string } | null;
  encl: string;
  // Tier 2
  justify: { mode: string; line: string } | null;
  ppeMode: string;
  boundary: string;
  standby: string;
  peak: string;
  meterVerify: boolean;
  doVerb: string;
  technique: string[];
  // Tier 3 isolation (live PERMIT table)
  sources: string;
  load: string;
  stored: string;
  backfeed: string;
  nonElecDefault: string[];
  /** The task's example job brief, shown as the placeholder (JSP cleanup PR 1, C2). */
  briefExample: string;
}

/** Other hazardous energy (Robert 2026-10-09). Hazards that are not energy sources came off. Past JSPs keep what was picked. */
export const ENERGY_OPTIONS = ["Mechanical", "Pneumatic", "Hydraulic", "Steam", "Chemical", "Other"] as const;
export const RAISE_REASONS = ["Conditions Worse Than The Record", "Access Or Work Area", "Unfamiliar Equipment", "Scope Changed", "Other"] as const;
export const NEED_POWER_REASONS = ["Reading Must Be Under Load", "Fault Only Shows Energized", "Other"] as const;
export const TIER_LABEL: Record<1 | 2 | 3, string> = { 1: "Tier 1 · Low Exposure Task", 2: "Tier 2 · Diagnostic With Exposure", 3: "Tier 3 · De-energized (ESWC)" };
/** Locked node subtitles, title case on screen (JSP_ADJUSTMENTS item 28). */
export const NODE_SUB: Record<string, string> = {
  Job: "Job brief",
  "Fit For Duty": "Fit for duty",
  "Need Power?": "Does it need to be energized?",
  "Field Assessment": "Check before you touch",
  Risk: "Recognize the risk",
  "Isolation Plan": "What energy is here and where it locks",
  "PPE And Boundary": "Boundary · PPE",
  "Lock Out": "Isolate and lock every source",
  "Test Dead": "Prove the Zero Energy State",
  "Do The Work": "Perform the task",
  "Sign Off": "Submit the plan",
};

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
/** JSP cleanup PR 1 (C1): screen text carries no em dashes. A spaced dash becomes a period and the next word is capitalized, or a colon inside a question. Task names are keys and are never tidied. */
export function tidy(x: string): string {
  // A question keeps reading as one question. The dash becomes a colon: "Do you know the cause: overload, or a fault?"
  if (x.trim().endsWith("?")) return x.replace(/\s*—\s*/g, ": ");
  return x.replace(/\s+—\s+(\S)/g, (_m, c: string) => `. ${c.toUpperCase()}`).replace(/\.\.\s/g, ". ").replace(/\s*—\s*/g, ". ");
}
/** A technique line that repeats the meter tick (JSP cleanup PR 1, A5). */
const METER_REPEAT = /^CAT III\/IV meter for the voltage/;

function promptsOf(v: unknown, taskName: string): JspPrompt[] {
  const place = PROMPT_PLACE[taskName] ?? {};
  const live: JspPrompt[] = (Array.isArray(v) ? v : [])
    .filter((p): p is Record<string, unknown> => Boolean(p) && typeof p === "object")
    .filter((p) => !isDraft(String(p.q ?? "")))
    .filter((p) => !place[str(p.k)]?.drop)
    .map((p) => {
      const o = place[str(p.k)] ?? {};
      const bad: PromptAnswer = p.bad === "yes" ? "yes" : "no";
      return { k: str(p.k), q: tidy(o.q ?? str(p.q)), opts: ["yes", "no"], bad: [bad], why: tidy(o.why ?? str(p.why)), escal: o.escal ?? (p.escal === true || o.deen === true), at: o.at ?? "place", deen: o.deen === true };
    });
  const have = new Set(live.map((p) => p.k));
  return live.concat((PROMPT_ADD[taskName] ?? []).filter((p) => !have.has(p.k)));
}

/** True when this answer shows the question's why line. */
export function isBad(p: JspPrompt, ans: PromptAnswer | undefined): boolean {
  return ans !== undefined && p.bad.includes(ans);
}

/** The JSP's view of one task: the task record plus its live content. */
export function jspTaskOf(task: { taskId: string; name: string; tier: number; taskType?: string; sopNumber?: string | null }): JspTask {
  const c = (LIVE_TASK_CONTENT[task.name] ?? null) as Record<string, unknown> | null;
  const p = (c?.permit ?? {}) as Record<string, unknown>;
  const tier = (task.tier === 1 || task.tier === 2 || task.tier === 3 ? task.tier : 3) as 1 | 2 | 3;
  const tt = task.taskType;
  const taskType: VerdictTaskType = tt === "Observe" || tt === "Operate-open" || tt === "Operate-close" || tt === "Expose" ? tt : "Expose";
  const db = c?.doorBranch as JspTask["doorBranch"] | undefined;
  const j = c?.justify as { mode?: string; line?: string } | undefined;
  const fix = CONTROL_FIX[task.name] ?? {};
  const clean = (x: string) => (isDraft(x) ? "" : tidy(x));
  const lines = (v: unknown) => strs(v).filter((x) => !isDraft(x));
  return {
    taskId: task.taskId,
    name: task.name,
    tier,
    taskType,
    sopNumber: task.sopNumber ?? null,
    hasContent: c !== null,
    prompts: promptsOf(c?.prompts, task.name),
    handOn: HAND_ON.has(task.name),
    rabLine: RAB_TASKS.has(task.name),
    controls: lines(c?.controls).map((x) => tidy(fix[x] ?? x)),
    door: tidy(DOOR_FIX[task.name] ?? str(c?.door).replace(/\s*→\s*/g, ", ")),
    doorBranch: db && typeof db === "object" ? { q: tidy(db.q), reasons: db.reasons.map((r) => ({ v: r.v, label: tidy(r.label) })), affirm: tidy(db.affirm) } : null,
    encl: tidy(str(c?.encl)),
    justify: j ? { mode: str(j.mode), line: tidy(str(j.line)) } : null,
    ppeMode: str(c?.ppeMode) || "full",
    boundary: tidy(str(c?.boundary)),
    standby: clean(str(c?.standby)),
    peak: clean(str(c?.peak)),
    meterVerify: c?.meterVerify === true,
    doVerb: str(c?.doVerb) || "Do",
    technique: lines(c?.technique)
      .filter((x) => !(c?.meterVerify === true && METER_REPEAT.test(x)))
      .map(tidy),
    sources: clean(str(p.sources)),
    load: clean(str(p.load)),
    stored: clean(str(p.stored)),
    backfeed: clean(str(p.backfeed)),
    nonElecDefault: strs(p.nonElecDefault).filter((x) => (ENERGY_OPTIONS as readonly string[]).includes(x)),
    briefExample: clean(str(c?.brief)),
  };
}

// ---- The asset, as the JSP reads it ----

export interface JspAsset {
  assetId: string;
  equipmentType: string;
  area: string;
  siteName: string;
  isDemo: boolean;
  /** Numeric volts, or null when blank (never read as 0 V). */
  volts: number | null;
  incidentEnergy: number | "pending" | "outsideStudy";
  limitedApproach: string;
  arcFlashBoundary: string;
  /** Every asset reads Not Verified until the EMP build sets due dates (decision 40). */
  serviceability: "Serviceable" | "Not Verified" | "Limited Service" | "Nonserviceable";
  overdutyThisDevice: boolean;
  /** The site walk (node map) is not on Firebase yet: upstream reads not verified. */
  upstreamWalked: boolean;
  /** Program Settings IE Ceiling: the top stocked PPE. */
  ieCeilingCal: number;
}

/** The engine fields read from the asset record. Same on the page and on the server. */
export interface JspAssetRecord {
  volts: number | null;
  incidentEnergy: JspAsset["incidentEnergy"];
  limitedApproach: string;
  arcFlashBoundary: string;
  overdutyThisDevice: boolean;
  ieCeilingCal: number;
}

/** Group 1 in the approved mockup. Used only when Program Settings has no IE Ceiling. */
export const IE_CEILING_FALLBACK = 12;

export function assetRecordOf(d: AssetDoc, ieCeilingCal: unknown): JspAssetRecord {
  const raw = typeof d.nominalVolts === "number" ? d.nominalVolts : parseFloat(d.nominalVoltage ?? "");
  const volts = Number.isFinite(raw) && raw > 0 ? raw : null;
  const ie = typeof d.incidentEnergyCal === "number" && Number.isFinite(d.incidentEnergyCal) ? d.incidentEnergyCal : /outside/i.test(d.ieStatus ?? "") ? "outsideStudy" : "pending";
  const blank = (v: string | undefined) => (v ?? "").trim();
  return {
    volts,
    incidentEnergy: ie,
    limitedApproach: blank(d.limitedApproach),
    arcFlashBoundary: blank(d.arcFlashBoundary),
    overdutyThisDevice: d.isOverduty === true,
    ieCeilingCal: typeof ieCeilingCal === "number" && ieCeilingCal > 0 ? ieCeilingCal : IE_CEILING_FALLBACK,
  };
}

export function jspAssetOf(a: { assetId: string; equipmentType: string; area: string; siteName: string; isDemo: boolean }, r: JspAssetRecord): JspAsset {
  const clean = (v: string) => (v === "—" ? "" : v);
  return {
    assetId: a.assetId,
    equipmentType: clean(a.equipmentType),
    area: clean(a.area),
    siteName: a.siteName,
    isDemo: a.isDemo,
    ...r,
    serviceability: "Not Verified",
    upstreamWalked: false,
  };
}

export type PillTone = "green" | "amber" | "red" | "grey";

/** The glove pill takes the glove card's own words and color (JSP_WRITES_WORDS.md section 2). Null view: gloves not open yet. */
export function glovePillOf(view: GloveView | null, today: string): { label: string; tone: PillTone } {
  if (!view) return { label: "Gloves open after Node 3", tone: "grey" };
  const card = gloveCardOf(view, today);
  const tone: PillTone = card.dashTone === "ok" ? "green" : card.dashTone === "warn" ? "amber" : card.dashTone === "bad" ? "red" : "grey";
  return { label: card.dashLine, tone };
}

/**
 * The training pill reads his node records (section 5). Red when a node has expired, amber with the
 * date when one expires within 30 days, green otherwise. A node not done yet does not change it.
 */
export function trainingPillOf(card: QewTrainingCard): { label: string; tone: PillTone } {
  if (card.nodes.some((n) => n.state === "expired")) return { label: "Training expired", tone: "red" };
  const soon = card.nodes
    .filter((n) => n.state === "expiringSoon" && n.expiresOn)
    .map((n) => n.expiresOn!)
    .sort()[0];
  if (soon) return { label: `Training due ${usDate(soon)}`, tone: "amber" };
  return { label: "Training current", tone: "green" };
}

/** The two pills on the black bar: gloves, then training. Information only. The JSP always runs. */
export function jspPillsOf(training: QewTrainingCard, gloves: GloveView | null, today: string): { label: string; tone: PillTone }[] {
  return [glovePillOf(gloves, today), trainingPillOf(training)];
}

// ---- The QEW's answers ----

export interface JspState {
  phase: string;
  brief: string;
  fit: "YES" | "NO" | null;
  plan: "dead" | "live" | null;
  planWhy: string | null;
  /** Acknowledged DANGER at Risk: the rail turns de-energized. */
  deen: boolean;
  raising: boolean;
  hz: "shock" | "arc" | "both" | null;
  lv: RaiseLevel | null;
  rs: string | null;
  raised: { hz: "shock" | "arc" | "both"; lv: RaiseLevel; rs: string } | null;
  whyOpen: boolean;
  whyRead: boolean;
  cond: {
    sense: boolean | null;
    senses: string[];
    doors: boolean | null;
    covers: boolean | null;
    damage: boolean | null;
    dust: boolean | null;
    tamper: "intact" | "broken" | "none" | null;
    near: boolean | null;
    line: boolean | null;
  };
  prompts: Record<string, PromptAnswer>;
  escalAck: Record<string, boolean>;
  door: "open" | "closed" | null;
  reason: string | null;
  /** Confirm rows: boundary, poster, ppeDonned, ppeGloves, encl, area, irLoad, ... */
  c: Record<string, boolean>;
  irCover: "yes" | "no" | null;
  standbyMode: "standby" | "alone" | null;
  notifySent: boolean;
  /** Isolation Plan: how many electrical sources feed it. 4 means four or more. */
  srcCount: 1 | 2 | 3 | 4 | null;
  grounding: boolean | null;
  /** Decision 65, Tier 3: will anything in the work area stay energized? */
  liveRemain: boolean | null;
  /** Lock Out steps (LOCK_STEPS) and Test Dead grounds. */
  s2: Record<string, boolean>;
  /** Other hazardous energy picked at the Isolation Plan. */
  energy: Record<string, boolean>;
  energyNone: boolean;
  /** Each picked type confirmed controlled at Lock Out. */
  energyConfirm: Record<string, "confirmed">;
  ldl: number;
  gloves: "pass" | "fail" | null;
  /** On a Fail: what he found (GLOVE_DEFECTS), and the line he typed for Other. */
  gloveDefect: string | null;
  gloveDefectNote: string;
  closing: boolean;
  submitted: boolean;
  /**
   * EEWP approved and PPE confirmed (EEWP_WORDS.md rev 3). Never from the page's answers: the page sets it
   * from the permit, the server from its own record. The verdict reads it only for IE above 40 (decision 41).
   */
  eewpApproved: boolean;
  /** Where the approved EEWP was asked. Lock Out means the Tier 3 job is done energized under the permit. */
  eewpFrom: string | null;
}

export function newJspState(task: JspTask): JspState {
  const energy: Record<string, boolean> = {};
  for (const o of task.nonElecDefault) energy[o] = true;
  return {
    phase: "Job",
    brief: "",
    fit: null,
    plan: null,
    planWhy: null,
    deen: false,
    raising: false,
    hz: null,
    lv: null,
    rs: null,
    raised: null,
    whyOpen: false,
    whyRead: false,
    cond: { sense: null, senses: [], doors: null, covers: null, damage: null, dust: null, tamper: null, near: null, line: null },
    prompts: {},
    escalAck: {},
    door: null,
    reason: null,
    c: {},
    irCover: null,
    standbyMode: null,
    notifySent: false,
    srcCount: null,
    grounding: null,
    liveRemain: null,
    s2: {},
    energy,
    energyNone: false,
    energyConfirm: {},
    ldl: 0,
    gloves: null,
    gloveDefect: null,
    gloveDefectNote: "",
    closing: false,
    submitted: false,
    eewpApproved: false,
    eewpFrom: null,
  };
}

// ---- Rules ----

/** The Field Assessment answers as the engine reads them. An unanswered question is no finding. */
export function fieldAssessmentOf(s: JspState): FieldAssessment {
  const c = s.cond;
  return {
    sensesNoticed: c.sense ? c.senses : [],
    doorsClosedAndLatched: c.doors !== false,
    coversAndDeadfrontInPlace: c.covers !== false,
    damageBurnMarksOrDiscoloration: c.damage === true,
    dustMoistureOrDebris: c.dust === true,
    tamperSticker: c.tamper ?? "intact",
    handsWithin12In: c.near === true,
    lineSideExposed: c.line === true,
  };
}

export function verdictInputOf(a: JspAsset, t: JspTask, s: JspState): VerdictInput {
  return {
    voltage: a.volts,
    incidentEnergyCal: a.incidentEnergy,
    serviceability: a.serviceability,
    overdutyThisDevice: a.overdutyThisDevice,
    upstream: a.upstreamWalked ? { state: "walkedClear" } : { state: "notWalked" },
    topStockedPpeCal: a.ieCeilingCal,
    approvedEewp: s.eewpApproved,
    tier: t.tier,
    taskType: t.taskType,
    needPower:
      t.tier !== 2 ? null : scopeDead(t, s) ? "deEnergize" : energizedByNature(t) ? "needsPower" : s.plan === "dead" ? "deEnergize" : s.plan === "live" ? "needsPower" : null,
    fieldAssessment: fieldAssessmentOf(s),
    firstOperationAfterWork: firstOperationAfterWork(t, s),
    qewRaise: s.raised ? { hazard: s.raised.hz, level: s.raised.lv, reason: s.raised.rs } : null,
  };
}

export function verdictOf(a: JspAsset, t: JspTask, s: JspState): Verdict {
  return verdict(verdictInputOf(a, t, s));
}

/** Decision 64: Yes or Don't Know on the first-operation question. Never on Tier 3 (its re-energize checks cover it). */
export function firstOperationAfterWork(t: JspTask, s: JspState): boolean {
  const p = t.prompts.find((x) => x.k === FIRST_OPERATION_KEY);
  return t.tier !== 3 && p !== undefined && isBad(p, s.prompts[p.k]);
}

/** Decision 65: Tier 3 asks whether live parts stay in the work area. */
export function asksWorkArea(t: JspTask): boolean {
  return t.tier === 3;
}

/** Decision 65: PPE stays on for the whole job. */
export function liveRemains(t: JspTask, s: JspState): boolean {
  return asksWorkArea(t) && s.liveRemain === true;
}

/** Decision 62 item 4: an Observe task on energized work stays at the door plane, hands outside the RAB. */
export function doorPlane(t: JspTask, s: JspState): boolean {
  return t.taskType === "Observe" && !isDead(t, s);
}

/** IR (inherent) and Disconnect Operation (switching): Need Power? confirms, it does not ask. */
export function energizedByNature(t: JspTask): boolean {
  return t.justify?.mode === "inherent" || t.justify?.mode === "switching";
}

/** The question whose bad answer turned this job de-energized with De-Energize This Job, or null. */
export function scopeDeenPrompt(t: JspTask, s: JspState): JspPrompt | null {
  return t.prompts.find((p) => p.deen && isBad(p, s.prompts[p.k]) && s.escalAck[p.k]) ?? null;
}
export function scopeDead(t: JspTask, s: JspState): boolean {
  return scopeDeenPrompt(t, s) !== null;
}

/** De-energized work: Tier 3, Need Power? Yes, De-Energize This Job, or DANGER acknowledged at Risk. */
export function isDead(t: JspTask, s: JspState): boolean {
  // An approved EEWP asked at Lock Out turns the Tier 3 job energized, under the permit.
  const tier3Dead = t.tier === 3 && !(s.eewpApproved && s.eewpFrom === "lockOut");
  return tier3Dead || s.plan === "dead" || s.deen || scopeDead(t, s);
}

export function railOf(t: JspTask, s: JspState): string[] {
  const r = ["Job", "Fit For Duty"];
  if (t.tier === 2) r.push("Need Power?");
  r.push("Field Assessment", "Risk");
  if (isDead(t, s)) return r.concat(["Isolation Plan", "PPE And Boundary", "Lock Out", "Test Dead", "Sign Off"]);
  r.push("PPE And Boundary");
  if (t.tier === 2) r.push("Do The Work");
  r.push("Sign Off");
  return r;
}

export function condDone(t: JspTask, s: JspState): boolean {
  const c = s.cond;
  const ex = t.taskType === "Expose";
  return (
    c.sense !== null &&
    (c.sense === false || c.senses.length > 0) &&
    c.doors !== null &&
    c.covers !== null &&
    c.damage !== null &&
    c.dust !== null &&
    c.tamper !== null &&
    (!ex || (c.near !== null && c.line !== null))
  );
}

export function findings(s: JspState): string[] {
  const c = s.cond;
  const f: string[] = [];
  if (c.sense) f.push(c.senses.join(", ").toLowerCase());
  if (c.doors === false) f.push("doors not closed and latched");
  if (c.covers === false) f.push("covers or deadfront missing");
  if (c.damage) f.push("damage, burn marks, or discoloration");
  if (c.dust) f.push("dust, moisture, or debris");
  return f;
}

/**
 * Task questions show where they act (rev 9, amends decision 60).
 * start: Job, every rail. need: Need Power?. place: Isolation Plan on Tier 3, PPE (Tier 1) or Do The Work (Tier 2)
 * on energized work only.
 */
export function promptsFor(t: JspTask, s: JspState, at: JspPrompt["at"] = "place"): JspPrompt[] {
  const here = t.prompts.filter((p) => p.at === at);
  if (at !== "place" || t.tier === 3) return here;
  return isDead(t, s) ? [] : here;
}

export function escalPending(t: JspTask, s: JspState, at: JspPrompt["at"] = "place"): boolean {
  return promptsFor(t, s, at).some((p) => p.escal && isBad(p, s.prompts[p.k]) && !s.escalAck[p.k]);
}

export function promptsDone(t: JspTask, s: JspState, at: JspPrompt["at"] = "place"): boolean {
  return promptsFor(t, s, at).every((p) => Boolean(s.prompts[p.k]));
}

export function energyList(s: JspState): string[] {
  return ENERGY_OPTIONS.filter((o) => s.energy[o]);
}

/** Isolation Plan: other energy picked, or None. */
export function energyOk(s: JspState): boolean {
  return s.energyNone || energyList(s).length > 0;
}

/** Lock Out: every step and every picked energy type confirmed. */
export function lockOutDone(s: JspState): boolean {
  return LOCK_STEPS.every((k) => s.s2[k]) && energyList(s).every((o) => s.energyConfirm[o] === "confirmed");
}

/** The glove question is answered: Pass, or Fail with what he found. */
export function glovesAnswered(s: JspState): boolean {
  return s.gloves === "pass" || (s.gloves === "fail" && gloveFailProblem(s.gloveDefect, s.gloveDefectNote) === null);
}

/** Tier 1 glove inspection: a hand on the equipment, or a door open. Only at 50 V or more, or voltage unknown. */
export function tier1Gloves(a: JspAsset, t: JspTask, s: JspState): boolean {
  return (a.volts === null || a.volts >= 50) && (t.handOn || s.door === "open");
}

/** "4+" for four or more. */
export function sourceCountText(n: JspState["srcCount"]): string {
  return n === null ? "" : n >= 4 ? "4+" : String(n);
}

/** The LDL rehearsal for this asset's voltage (decision 57). */
export function ldlStepsOf(a: JspAsset) {
  return ldlSequence(a.volts);
}

/** Edit on a finished node. Going back to Risk or earlier clears the Risk acknowledgment (JSP_ADJUSTMENTS 24, 26). */
export function editTo(t: JspTask, s: JspState, node: string): JspState {
  const rail = railOf(t, s);
  const back = rail.indexOf(node) <= rail.indexOf("Risk");
  return { ...s, phase: node, closing: false, ...(back ? { deen: false, whyRead: false, whyOpen: false } : {}) };
}

export function nextPhase(t: JspTask, s: JspState): JspState {
  const rail = railOf(t, s);
  const i = rail.indexOf(s.phase);
  return { ...s, phase: rail[Math.min(i + 1, rail.length - 1)] ?? s.phase, closing: false };
}

/** The done-pill line for a finished node. */
export function doneLine(a: JspAsset, t: JspTask, s: JspState, node: string): string {
  const v = verdictOf(a, t, s);
  switch (node) {
    case "Job":
      return t.name;
    case "Fit For Duty":
      return "Confirmed. Focused and alert";
    case "Need Power?":
      return t.justify?.mode === "inherent"
        ? "Energized and loaded ≥ 40%"
        : t.justify?.mode === "switching"
          ? "Load interrupted before the throw"
          : scopeDead(t, s) || s.plan === "dead"
            ? "Can do it de-energized"
            : `Needs power: ${s.planWhy ?? ""}`;
    case "Field Assessment": {
      const f = findings(s);
      return f.length ? `Recorded: ${f.join("; ")}` : "No findings";
    }
    case "Risk":
      return `Acknowledged · Shock ${v.shockLevel} · Arc ${v.arcLevel}${s.deen ? " · De-energize" : ""}`;
    case "Isolation Plan": {
      const n = sourceCountText(s.srcCount);
      const other = energyList(s);
      return `${n} source${n === "1" ? "" : "s"} identified${s.grounding ? ", grounds required" : ""}${liveRemains(t, s) ? ", live parts remain" : ""}${other.length ? ` · ${other.join(", ")}` : ""}`;
    }
    case "PPE And Boundary":
      return isDead(t, s)
        ? "Boundary set · PPE for switching and LDL"
        : t.tier === 1
          ? s.door === "open"
            ? "Door open. Boundary for others"
            : "Controls confirmed"
          : `Boundary set · PPE on · ${s.standbyMode === "alone" ? "personnel notified" : "standby"}`;
    case "Lock Out":
      return `Locked and tried · ${sourceCountText(s.srcCount)} source${s.srcCount === 1 ? "" : "s"}`;
    case "Test Dead": {
      const n = ldlStepsOf(a).length;
      return `LDL rehearsed, ${n} of ${n}`;
    }
    case "Do The Work":
      return `${t.doVerb} · meter and technique`;
    default:
      return NODE_SUB[node] ?? node;
  }
}

// ---- What the JSP records at Submit ----

/** The answers the server stores with the JSP. Plain data only. The server recomputes the verdict. */
export interface JspAnswers {
  brief: string;
  fit: "YES";
  plan: "dead" | "live" | null;
  planWhy: string | null;
  deen: boolean;
  raised: JspState["raised"];
  cond: JspState["cond"];
  prompts: Record<string, PromptAnswer>;
  escalAck: string[];
  door: "open" | "closed" | null;
  doorReason: string | null;
  confirms: string[];
  irCover: "yes" | "no" | null;
  standbyMode: "standby" | "alone" | null;
  notifySent: boolean;
  grounding: boolean | null;
  /** Decision 65, Tier 3: live parts stay in the work area, so PPE stays on for the whole job. */
  workAreaLive: boolean | null;
  isolationChecks: string[];
  /** Isolation Plan: 1, 2, 3, or 4 for four or more. */
  sourceCount: 1 | 2 | 3 | 4 | null;
  otherEnergy: { type: string; answer: "confirmed" }[];
  otherEnergyNone: boolean;
  ldlRehearsed: number;
  gloves: "pass" | "fail" | null;
  /** On a Fail: what he found (GLOVE_DEFECTS), and the line he typed for Other. */
  gloveDefect: string | null;
  gloveDefectNote: string;
}

export function answersOf(s: JspState): JspAnswers {
  const on = (r: Record<string, boolean>) => Object.keys(r).filter((k) => r[k]).sort();
  return {
    brief: s.brief.trim().slice(0, 2000),
    fit: "YES",
    plan: s.plan,
    planWhy: s.planWhy,
    deen: s.deen,
    raised: s.raised,
    cond: s.cond,
    prompts: s.prompts,
    escalAck: on(s.escalAck),
    door: s.door,
    doorReason: s.reason,
    confirms: on(s.c),
    irCover: s.irCover,
    standbyMode: s.standbyMode,
    notifySent: s.notifySent,
    grounding: s.grounding,
    workAreaLive: s.liveRemain,
    isolationChecks: on(s.s2),
    sourceCount: s.srcCount,
    otherEnergy: energyList(s)
      .filter((type) => s.energyConfirm[type] === "confirmed")
      .map((type) => ({ type, answer: "confirmed" as const })),
    otherEnergyNone: s.energyNone,
    ldlRehearsed: s.ldl,
    gloves: s.gloves,
    gloveDefect: s.gloves === "fail" ? s.gloveDefect : null,
    gloveDefectNote: s.gloves === "fail" && s.gloveDefect === "Other" ? s.gloveDefectNote.trim().slice(0, 300) : "",
  };
}

/** The server's check before it saves a JSP. Null when it can be saved. */
export function submitProblem(t: JspTask, a: JspAnswers): string | null {
  if (!t.hasContent) return `${t.name} has no JSP content yet. See your ESP Manager.`;
  if (!a.brief) return "Add a job brief.";
  if (a.fit !== "YES") return "Fit for duty is required.";
  const scope = t.prompts.some((p) => p.deen && isBad(p, a.prompts[p.k]) && a.escalAck.includes(p.k));
  if (t.tier === 2 && !energizedByNature(t) && !scope && !a.plan) return "Answer Need Power?.";
  if (a.plan === "live" && !a.planWhy) return "Pick why it needs power.";
  if (asksWorkArea(t) && a.workAreaLive === null) return "Answer whether anything in your work area stays energized.";
  if (a.gloves === "fail") {
    const g = gloveFailProblem(a.gloveDefect, a.gloveDefectNote);
    if (g) return g;
  }
  return null;
}

/** JSP number from the counter: JSP-0001. */
export function jspNumberOf(n: number): string {
  return `JSP-${String(n).padStart(4, "0")}`;
}

export const CLOSE_WORK = ["done", "stopped"] as const;
export const CLOSE_LEFT = ["same", "fixed", "worse"] as const;
export const CLOSE_ISOLATION = ["rts", "locked"] as const;

export interface JspClose {
  work: (typeof CLOSE_WORK)[number];
  leftAs: (typeof CLOSE_LEFT)[number];
  note: string;
  isolation: (typeof CLOSE_ISOLATION)[number] | null;
  /** Returned To Service: the four re-energize checks (rev 9 item 6). */
  reEnergizeChecks?: string[];
}

/** The close pill (decision 59). Isolation is asked on de-energized work only. Returned To Service needs all four checks. */
export function closeProblem(c: Partial<JspClose>, dead: boolean): string | null {
  if (!c.work || !(CLOSE_WORK as readonly string[]).includes(c.work)) return "Is the work done?";
  if (!c.leftAs || !(CLOSE_LEFT as readonly string[]).includes(c.leftAs)) return "How did you leave the equipment?";
  if (dead && (!c.isolation || !(CLOSE_ISOLATION as readonly string[]).includes(c.isolation))) return "Where did you leave the isolation?";
  const got = Array.isArray(c.reEnergizeChecks) ? c.reEnergizeChecks : [];
  if (dead && c.isolation === "rts" && !REENERGIZE_CHECKS.every(([k]) => got.includes(k))) return "Confirm every check before you re-energize.";
  return null;
}

// ---- The server's read of what the page sent ----

const pick = <T extends string>(v: unknown, list: readonly T[]): T | null => (typeof v === "string" && (list as readonly string[]).includes(v) ? (v as T) : null);
const bool = (v: unknown): boolean | null => (v === true ? true : v === false ? false : null);
const short = (v: unknown, n = 200): string => (typeof v === "string" ? v.trim().slice(0, n) : "");
const keyList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && x.length <= 40).slice(0, 40) : []);

/** The answers from the page, cleaned. Anything unknown is dropped. Null when it is not an answers object. */
export function answersFrom(raw: unknown): JspAnswers | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const c = (r.cond && typeof r.cond === "object" ? r.cond : {}) as Record<string, unknown>;
  const rz = (r.raised && typeof r.raised === "object" ? r.raised : null) as Record<string, unknown> | null;
  const hz = rz ? pick(rz.hz, ["shock", "arc", "both"] as const) : null;
  const lv = rz ? pick(rz.lv, ["MEDIUM", "HIGH", "DANGER"] as const) : null;
  const rs = rz ? pick(rz.rs, RAISE_REASONS) : null;
  const prompts: Record<string, PromptAnswer> = {};
  if (r.prompts && typeof r.prompts === "object") {
    for (const [k, v] of Object.entries(r.prompts as Record<string, unknown>).slice(0, 40)) {
      const a = pick(v, ["yes", "no", "dontKnow"] as const);
      if (a && k.length <= 40) prompts[k] = a;
    }
  }
  const other = Array.isArray(r.otherEnergy) ? r.otherEnergy : [];
  return {
    brief: short(r.brief, 2000),
    fit: r.fit === "YES" ? "YES" : (null as unknown as "YES"),
    plan: pick(r.plan, ["dead", "live"] as const),
    planWhy: pick(r.planWhy, NEED_POWER_REASONS),
    deen: r.deen === true,
    raised: hz && lv && rs ? { hz, lv, rs } : null,
    cond: {
      sense: bool(c.sense),
      senses: keyList(c.senses).filter((x) => ["Smell", "Noise", "Heat", "Arcing"].includes(x)),
      doors: bool(c.doors),
      covers: bool(c.covers),
      damage: bool(c.damage),
      dust: bool(c.dust),
      tamper: pick(c.tamper, ["intact", "broken", "none"] as const),
      near: bool(c.near),
      line: bool(c.line),
    },
    prompts,
    escalAck: keyList(r.escalAck),
    door: pick(r.door, ["open", "closed"] as const),
    doorReason: short(r.doorReason, 40) || null,
    confirms: keyList(r.confirms),
    irCover: pick(r.irCover, ["yes", "no"] as const),
    standbyMode: pick(r.standbyMode, ["standby", "alone"] as const),
    notifySent: r.notifySent === true,
    grounding: bool(r.grounding),
    workAreaLive: bool(r.workAreaLive),
    isolationChecks: keyList(r.isolationChecks),
    otherEnergy: other
      .map((o) => (o && typeof o === "object" ? (o as Record<string, unknown>) : {}))
      .map((o) => ({ type: pick(o.type, ENERGY_OPTIONS), answer: pick(o.answer, ["confirmed"] as const) }))
      .filter((o): o is { type: (typeof ENERGY_OPTIONS)[number]; answer: "confirmed" } => Boolean(o.type && o.answer)),
    sourceCount: r.sourceCount === 1 || r.sourceCount === 2 || r.sourceCount === 3 || r.sourceCount === 4 ? r.sourceCount : null,
    otherEnergyNone: r.otherEnergyNone === true,
    ldlRehearsed: typeof r.ldlRehearsed === "number" && Number.isInteger(r.ldlRehearsed) ? Math.max(0, Math.min(8, r.ldlRehearsed)) : 0,
    gloves: pick(r.gloves, ["pass", "fail"] as const),
    gloveDefect: pick(r.gloveDefect, GLOVE_DEFECTS),
    gloveDefectNote: short(r.gloveDefectNote, 300),
  };
}

/** Rebuilds the parts of the state the verdict reads, so the server can recompute it. */
export function stateFromAnswers(t: JspTask, a: JspAnswers): JspState {
  const escalAck: Record<string, boolean> = {};
  for (const k of a.escalAck) escalAck[k] = true;
  return { ...newJspState(t), brief: a.brief, fit: a.fit, plan: a.plan, planWhy: a.planWhy, deen: a.deen, raised: a.raised, cond: a.cond, prompts: a.prompts, escalAck, grounding: a.grounding, liveRemain: a.workAreaLive };
}

/** The record fields the server stores from the verdict. Levels and the isolation point as text. */
export function verdictRecordOf(v: Verdict, dead: boolean): Record<string, unknown> {
  return {
    shockLevel: v.shockLevel,
    arcLevel: v.arcLevel,
    topLevel: v.topLevel,
    isDeEnergized: dead,
    isolationPoint: v.isolationPoint.kind,
    followUp: v.followUp,
  };
}

// ---- The draft (QEW Page Return Path, approved 2026-10-09) ----
// The page saves the draft each time a node is finished, so Back To The QEW Page and Resume pick up
// where he left off on any device. The server never reads a verdict from a draft.

/** The one confirm for cancelling a JSP before Submit. Same words on the QEW page and inside the JSP. */
export function cancelConfirmText(jspNumber: string, taskName: string, startedTime: string): { title: string; body: string } {
  const n = jspNumber.replace("JSP-", "");
  return {
    title: `Cancel JSP #${n}?`,
    body: `${taskName}${startedTime ? `, started ${startedTime}` : ""}. Nothing has been submitted and no email goes out. It stays on record as cancelled.`,
  };
}

export interface JspDraft {
  answers: JspAnswers;
  /** Other energy picked at the Isolation Plan, confirmed or not. */
  energyPicked: string[];
  /** The node he was on. */
  phase: string;
  /** Fit For Duty answered Yes. The answers always read YES, so the draft keeps its own flag. */
  fitConfirmed: boolean;
}

export function draftOf(s: JspState): JspDraft {
  return { answers: answersOf(s), energyPicked: energyList(s), phase: s.phase, fitConfirmed: s.fit === "YES" };
}

/** The draft from the page, cleaned. Null when it is not a draft. */
export function draftFrom(raw: unknown): JspDraft | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const answers = answersFrom(r.answers);
  if (!answers) return null;
  const picked = Array.isArray(r.energyPicked) ? r.energyPicked.filter((x): x is string => typeof x === "string" && (ENERGY_OPTIONS as readonly string[]).includes(x)) : [];
  const phase = typeof r.phase === "string" && Object.prototype.hasOwnProperty.call(NODE_SUB, r.phase) ? r.phase : "Job";
  return { answers, energyPicked: picked, phase, fitConfirmed: r.fitConfirmed === true };
}

/** The page state rebuilt from a saved draft. A phase the rail no longer has goes back to Job. */
export function stateFromDraft(t: JspTask, d: JspDraft | null): JspState {
  const base = newJspState(t);
  if (!d) return base;
  const a = d.answers;
  const flags = (keys: string[]) => Object.fromEntries(keys.map((k) => [k, true])) as Record<string, boolean>;
  const energy: Record<string, boolean> = {};
  for (const o of d.energyPicked) energy[o] = true;
  const energyConfirm: Record<string, "confirmed"> = {};
  for (const o of a.otherEnergy) if (energy[o.type]) energyConfirm[o.type] = "confirmed";
  const s: JspState = {
    ...stateFromAnswers(t, a),
    fit: d.fitConfirmed ? "YES" : null,
    door: a.door,
    reason: a.doorReason,
    c: flags(a.confirms),
    irCover: a.irCover,
    standbyMode: a.standbyMode,
    notifySent: a.notifySent,
    srcCount: a.sourceCount,
    s2: flags(a.isolationChecks),
    energy: d.energyPicked.length || a.otherEnergyNone ? energy : base.energy,
    energyNone: a.otherEnergyNone,
    energyConfirm,
    ldl: a.ldlRehearsed,
    gloves: a.gloves,
    gloveDefect: a.gloveDefect,
    gloveDefectNote: a.gloveDefectNote,
  };
  const rail = railOf(t, s);
  const at = rail.indexOf(d.phase);
  // Past Risk means he already acknowledged it. Before Risk, he reads How It Got There again.
  return { ...s, phase: at < 0 ? "Job" : d.phase, whyRead: at > rail.indexOf("Risk") };
}
